// Menu lateral em grupos (Início, Atendimento, Consultas, Conteúdo, Contratação, Gestão, Administração).
// Cada usuário vê só o que o perfil e as filas permitem; os grupos abrem e fecham (a escolha fica guardada)
// e o grupo da tela atual fica sempre aberto. Com o menu recolhido, aparecem só os ícones.
import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import { Badge, Collapse, Tooltip, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import ViewColumnOutlinedIcon from "@material-ui/icons/ViewColumnOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import VerifiedUserOutlinedIcon from "@material-ui/icons/VerifiedUserOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import StoreOutlinedIcon from "@material-ui/icons/StoreOutlined";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import AssessmentOutlinedIcon from "@material-ui/icons/AssessmentOutlined";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import MenuBookOutlinedIcon from "@material-ui/icons/MenuBookOutlined";
import ContactsOutlinedIcon from "@material-ui/icons/ContactsOutlined";
import TimerOutlinedIcon from "@material-ui/icons/TimerOutlined";
import api from "../services/api";
import suporteApi from "../suporte/api";
import { tem, usePermissoes } from "../suporte/permissoes";
import { EVENTO_FORMULARIOS } from "../suporte/pages/FormulariosValidar";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";

const CHAVE = "menuGruposFechados";

const useStyles = makeStyles((theme) => ({
  grupo: {
    display: "flex", alignItems: "center", width: "100%", border: 0, background: "none", cursor: "pointer",
    padding: "10px 16px 4px", font: "inherit", fontSize: 11, fontWeight: 700, letterSpacing: ".08em",
    textTransform: "uppercase", color: theme.palette.text.secondary,
    "&:hover": { color: theme.palette.text.primary },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  },
  seta: { marginLeft: "auto", fontSize: 18, transition: "transform .15s" },
  fechada: { transform: "rotate(-90deg)" },
  item: { paddingTop: 6, paddingBottom: 6, borderLeft: "3px solid transparent" },
  ativo: {
    borderLeftColor: theme.palette.primary.main,
    backgroundColor: theme.palette.type === "dark" ? "rgba(133,196,64,.14)" : "rgba(133,196,64,.16)",
    "& .MuiListItemText-primary": { fontWeight: 600, color: theme.palette.primary.main },
    "& .MuiListItemIcon-root": { color: theme.palette.primary.main },
  },
  icone: { minWidth: 44 },
  texto: { "& .MuiListItemText-primary": { fontSize: 14 } },
}));

function ItemMenu({ icon, primary, to, badge, badgeCor = "error", recolhido }) {
  const c = useStyles();
  const { pathname } = useLocation();
  const ativo = to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`);
  const link = React.useMemo(() => React.forwardRef((p, ref) => <RouterLink to={to} ref={ref} {...p} />), [to]);
  const iconeComBadge = badge ? <Badge badgeContent={badge} color={badgeCor} max={99}>{icon}</Badge> : icon;
  const item = (
    <ListItem button component={link} className={`${c.item} ${ativo ? c.ativo : ""}`}>
      <ListItemIcon className={c.icone}>{iconeComBadge}</ListItemIcon>
      <ListItemText primary={primary} className={c.texto} />
    </ListItem>
  );
  return <li>{recolhido ? <Tooltip title={primary} placement="right">{item}</Tooltip> : item}</li>;
}

function Grupo({ titulo, rotas, fechados, alternar, recolhido, children }) {
  const c = useStyles();
  const { pathname } = useLocation();
  const itens = React.Children.toArray(children).filter(Boolean);
  if (!itens.length) return null;
  const temAtual = rotas.some((r) => (r === "/" ? pathname === "/" : pathname.startsWith(r)));
  const aberto = recolhido || temAtual || !fechados.includes(titulo);
  return (
    <>
      {recolhido ? <Divider style={{ margin: "6px 0" }} /> : (
        <button type="button" className={c.grupo} aria-expanded={aberto} onClick={(e) => { e.stopPropagation(); if (!temAtual) alternar(titulo); }}>
          {titulo}
          <ExpandMoreIcon className={`${c.seta} ${aberto ? "" : c.fechada}`} />
        </button>
      )}
      <Collapse in={aberto} timeout="auto">
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>{itens}</ul>
      </Collapse>
    </>
  );
}

const MainListItems = ({ drawerClose, drawerOpen = true }) => {
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const recolhido = !drawerOpen;

  const [fechados, setFechados] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CHAVE) || "[]");
    } catch {
      return [];
    }
  });
  const alternar = (g) => setFechados((atual) => {
    const novo = atual.includes(g) ? atual.filter((x) => x !== g) : [...atual, g];
    try {
      localStorage.setItem(CHAVE, JSON.stringify(novo));
    } catch {
      // sem armazenamento: só não lembra
    }
    return novo;
  });

  // permissões (mesmas regras de antes)
  const [docsOk, setDocsOk] = useState(false);
  const [filasUsuario, setFilasUsuario] = useState(null);
  useEffect(() => {
    suporteApi.get("/api/documentos/permissao").then((r) => setDocsOk(!!r.data.ok)).catch(() => {});
    suporteApi.get("/api/painel/filas").then((r) => setFilasUsuario(r.data)).catch(() => setFilasUsuario(null));
  }, []);
  // permissões granulares (suporte-app/app/permissoes.py) + regra de fila
  const permissoes = usePermissoes();
  const admin = user.profile === "admin";
  // sem resposta do suporte-app (fora do ar): administrador continua vendo tudo (para conseguir agir)
  const pode = (chave) => (permissoes ? tem(permissoes, chave) : admin);
  const gestao = pode("tickets.ver_todos");
  // equipe do suporte técnico: permissão + (admin ou quem tem fila que usa o card do suporte)
  const ehSuporte = pode("cards.ver_suporte")
    && (admin || !filasUsuario || !filasUsuario.length || filasUsuario.some((f) => !f.card_proprio));
  // cards das filas (ex.: Contratação → candidatos): permissão + (admin ou quem tem fila com card próprio)
  const temCardFila = pode("cards_fila.ver") && (admin || (filasUsuario || []).some((f) => f.card_proprio));

  // contadores do que pede ação
  const [paraValidar, setParaValidar] = useState(0);
  const [abertos, setAbertos] = useState(0);
  const [aguardando, setAguardando] = useState(0);
  useEffect(() => {
    const contar = () => {
      if (document.hidden) return;
      suporteApi.get("/api/formularios/respostas", { params: { status: "RESPONDIDO", por_pagina: 1 } })
        .then((r) => setParaValidar(r.data.total || 0)).catch(() => {});
      if (ehSuporte) suporteApi.get("/api/atendimentos").then((r) => setAbertos((r.data || []).length)).catch(() => {});
      api.get("/tickets", { params: { status: "pending", pageNumber: 1, showAll: gestao ? "true" : "false",
        queueIds: JSON.stringify((user.queues || []).map((q) => q.id)) } })
        .then((r) => setAguardando(r.data.count || 0)).catch(() => {});
    };
    contar();
    const t = setInterval(contar, 60000);
    window.addEventListener(EVENTO_FORMULARIOS, contar);
    return () => { clearInterval(t); window.removeEventListener(EVENTO_FORMULARIOS, contar); };
  }, [ehSuporte, gestao, user.queues]);

  const [conexaoCaiu, setConexaoCaiu] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setConexaoCaiu(whatsApps.some((w) => ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)));
    }, 2000);
    return () => clearTimeout(t);
  }, [whatsApps]);

  const g = { fechados, alternar, recolhido };
  const it = { recolhido };
  return (
    <div onClick={drawerClose}>
      <Grupo titulo="Início" rotas={["/"]} {...g}>
        {pode("gestao.dashboard") && <ItemMenu to="/" primary="Dashboard" icon={<DashboardOutlinedIcon />} {...it} />}
      </Grupo>
      <Grupo titulo="Atendimento" rotas={["/tickets", "/suporte/quadro", "/suporte/validar"]} {...g}>
        <ItemMenu to="/tickets" primary={i18n.t("mainDrawer.listItems.tickets")} icon={<WhatsAppIcon />} badge={aguardando} {...it} />
        {ehSuporte && <ItemMenu to="/suporte/quadro" primary="Quadro de atendimentos" icon={<ViewColumnOutlinedIcon />} badge={abertos} badgeCor="primary" {...it} />}
        <ItemMenu to="/suporte/validar" primary="Formulários para validar" icon={<PlaylistAddCheckIcon />} badge={paraValidar} badgeCor="secondary" {...it} />
      </Grupo>
      <Grupo titulo="Consultas" rotas={["/suporte/historico", "/suporte/protocolo", "/suporte/clientes", "/contacts"]} {...g}>
        {ehSuporte && <ItemMenu to="/suporte/historico" primary="Histórico" icon={<HistoryOutlinedIcon />} {...it} />}
        {ehSuporte && <ItemMenu to="/suporte/protocolo" primary="Consultar protocolo" icon={<VerifiedUserOutlinedIcon />} {...it} />}
        {ehSuporte && <ItemMenu to="/suporte/clientes" primary="Base clientes" icon={<StoreOutlinedIcon />} {...it} />}
        <ItemMenu to="/contacts" primary="Contatos / Técnicos" icon={<ContactPhoneOutlinedIcon />} {...it} />
      </Grupo>
      <Grupo titulo="Conteúdo" rotas={["/quickAnswers", "/suporte/manuais", "/suporte/formularios"]} {...g}>
        <ItemMenu to="/quickAnswers" primary={i18n.t("mainDrawer.listItems.quickAnswers")} icon={<QuestionAnswerOutlinedIcon />} {...it} />
        <ItemMenu to="/suporte/manuais" primary="Manuais" icon={<MenuBookOutlinedIcon />} {...it} />
        <ItemMenu to="/suporte/formularios" primary="Formulários (modelos)" icon={<AssignmentTurnedInOutlinedIcon />} {...it} />
      </Grupo>
      <Grupo titulo="Contratação" rotas={["/suporte/cards-fila", "/suporte/documentos"]} {...g}>
        {temCardFila && <ItemMenu to="/suporte/cards-fila" primary="Cards das filas" icon={<ContactsOutlinedIcon />} {...it} />}
        {docsOk && <ItemMenu to="/suporte/documentos" primary="Controle de documentos" icon={<DescriptionOutlinedIcon />} {...it} />}
      </Grupo>
      <Grupo titulo="Gestão" rotas={["/suporte/relatorios", "/suporte/metas"]} {...g}>
        {pode("gestao.relatorios") && <ItemMenu to="/suporte/relatorios" primary="Relatórios" icon={<AssessmentOutlinedIcon />} {...it} />}
        {pode("gestao.metas") && <ItemMenu to="/suporte/metas" primary="Metas de prazo" icon={<TimerOutlinedIcon />} {...it} />}
      </Grupo>
      <Grupo titulo="Administração" rotas={["/users", "/queues", "/connections", "/settings", "/suporte/listas", "/suporte/cards", "/suporte/importar"]} {...g}>
        {pode("usuarios.gerenciar") && <ItemMenu to="/users" primary={i18n.t("mainDrawer.listItems.users")} icon={<PeopleAltOutlinedIcon />} {...it} />}
        {pode("filas.gerenciar") && <ItemMenu to="/queues" primary={i18n.t("mainDrawer.listItems.queues")} icon={<AccountTreeOutlinedIcon />} {...it} />}
        {pode("conexoes.gerenciar") && <ItemMenu to="/connections" primary={i18n.t("mainDrawer.listItems.connections")} icon={<SyncAltIcon />} badge={conexaoCaiu ? "!" : 0} {...it} />}
        {pode("configuracoes") && <ItemMenu to="/settings" primary={i18n.t("mainDrawer.listItems.settings")} icon={<SettingsOutlinedIcon />} {...it} />}
        {pode("listas.gerenciar") && <ItemMenu to="/suporte/listas" primary="Listas" icon={<ListAltOutlinedIcon />} {...it} />}
        {pode("cards_por_fila.gerenciar") && <ItemMenu to="/suporte/cards" primary="Cards por fila" icon={<AssignmentTurnedInOutlinedIcon />} {...it} />}
        {pode("importar.planilha") && <ItemMenu to="/suporte/importar" primary="Importar planilha" icon={<CloudUploadOutlinedIcon />} {...it} />}
      </Grupo>
    </div>
  );
};

export default MainListItems;
